import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { useSEO } from '../hooks/useSEO';

const About = () => {
  useSEO({
    title: 'About Us',
    description: 'Learn the story behind Family Wishlist—a completely free, non-monetized platform built to bring families closer through simple, surprise-safe gift list sharing.',
    keywords: 'about family wishlist, gift list sharing, christmas gift coordinator, free family wishlist',
    schema: {
      '@context': 'https://schema.org',
      '@type': 'AboutPage',
      'name': 'About Family Wishlist',
      'description': 'The story and mission behind Family Wishlist—a simple, surprise-safe wishlist sharing tool.',
      'url': `${window.location.origin}/about`,
      'mainEntity': {
        '@type': 'WebSite',
        'name': 'Family Wishlist',
        'url': window.location.origin
      }
    }
  });

  return (
    <div className="max-w-4xl mx-auto py-12 px-4 sm:px-6 lg:px-8">
      <div className="mb-8">
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-xmas-green hover:text-xmas-red font-bold transition-colors"
        >
          <ArrowLeft size={20} /> Home
        </Link>
      </div>
      <h1 className="text-4xl font-bold text-xmas-red mb-8 text-center">About Family Wishlist</h1>

      <div className="prose prose-slate max-w-none space-y-6 text-gray-700">
        <section>
          <h2 className="text-2xl font-semibold text-xmas-green mb-3">Welcome</h2>
          <p>
            Family Wishlist was built with a simple, singular purpose: to make gift-giving organized, seamless, and full of surprises.
            Whether you are coordinating within a large extended family or just a small household, the site is designed to take the stress out of tracking who's purchased what, without spoiling the mystery of what is under the tree!
          </p>
        </section>

        <section>
          <h2 className="text-2xl font-semibold text-xmas-green mb-3">My Story</h2>
          <p>
            The site is developed and maintained by a single individual rather than a large corporation. I'm based in the UK, and currently lead a team of software engineers. This site helps me continue to develop my engineering skills, even when most of my day job revolves around being in meetings!
            I had the idea for the site many years ago, and in 2026, thought it was about time I made it properly. It was born from a desire to build a practical tool to benefit my own family at Christmas time.
            Because it is built with love and care, you can expect an experience focused entirely on the user.
          </p>
        </section>

        <section>
          <h2 className="text-2xl font-semibold text-xmas-green mb-3">A Simple Site</h2>
          <p>
            Family Wishlist deliberately leaves out heavy, distracting features like photo uploads or complex profile themes. This keeps the UI simple, but also means my database never contains your personal photos or personal details.
            Instead, I focus on what matters: a simple user experience to take the hastle out of list sharing.
            It just works, and hopefully works well! You can quickly add gift names, include links, and coordinate with your loved ones in a clean, uncluttered space.
          </p>
        </section>

        <section>
          <h2 className="text-2xl font-semibold text-xmas-green mb-3">100% Free, Zero Revenue</h2>
          <p>
            I respect your space and your wallet. Even when users add links to their favorite online stores, those links are never altered, tracked, or converted into affiliate links.
            Family Wishlist generates absolutely zero revenue. I do not sell ads, I do not monetise your data, and I will always ensure the site is 100% free to use for families everywhere.
          </p>
        </section>

        <section>
          <h2 className="text-2xl font-semibold text-xmas-green mb-3">Get in Touch</h2>
          <p>
            I hope Family Wishlist brings extra joy and ease to your family's celebrations.
            Whilst my original focus was on family (hence the site's name), at Christmas (hence the colour scheme), I hope you find it useful for individual lists or friend groups, at any time of the year!
            If you have any feedback, questions, or ideas to make the platform even better, I would love to hear from you!
            Please reach out at:
          </p>
          <p className="font-bold mt-2">
            <a href="mailto:info@family-wishlist.com" className="text-xmas-red hover:underline">
              info@family-wishlist.com
            </a>
          </p>
        </section>
      </div>
    </div>
  );
};

export default About;
